import { useQuery } from '@tanstack/react-query';
import { ChartPoint, ChartSeriesType, MetricChartDataType } from 'components/metric/chart';
import { viewerAPI } from 'api/viewer';
import { Highcharts } from 'components/chart/Chart';
import React, { lazy, useEffect, useMemo, useRef, useState } from 'react';
import { processMetricSeries } from 'components/metric/chart/seriesBuilder';
import { getAlertSeries } from 'components/metric/chart/series/getAlertSeries';
import { Chart as HChart } from 'highcharts';
import {
    AdditionalToDrawType,
    clearChart,
    drawSVG,
    RangeToDrawType,
    refreshSVGRangesGroup,
} from 'components/metric/chart/draw/drawRanges';
import chartRedrawAlerts from 'components/metric/chart/draw/chartRedrawAlerts';
import adjustCanvas from 'components/metric/chart/draw/adjustCanvas';
import Chart from 'components/chart/Chart';
import { useTheme } from '@mui/material/styles';
import { subtractRangeFromDate } from 'components/alert-rule-popup/parts/AlertRuleSimulationControl';
import { formatChartValue } from 'components/metric/chart/MetricChartWrapper';

export default function AlertRuleChart({
    elementId,
    segmentValueId,
    options,
}: {
    elementId: number;
    segmentValueId?: number;
    options?: {
        startDate?: Date;
        simulation_id?: number;
        unitToSimulate?: string;
        intToSimulate?: number;
    };
}) {
    const [chart, setChart] = useState<HChart>();
    const afterChartCreated = (chart: HChart) => setChart(chart);
    const appTheme = useTheme();

    const [simulationId, setSimulationId] = useState(0);
    useEffect(() => {
        setSimulationId(options?.simulation_id ?? 0);
    }, [options?.simulation_id]);

    const { data: chartData } = useQuery<MetricChartDataType, Error>({
        queryKey: ['alert_rule_chart_data', elementId, segmentValueId ?? 0, simulationId],
        queryFn: () => viewerAPI.loadChartData(elementId, segmentValueId ?? 0, 0, simulationId),
    });

    const [maxChartXValue, setMaxChartXValue] = useState<number>(0);
    const basicChartOptions: Highcharts.Options = {
        credits: { enabled: false },
        chart: {
            height: 182,
            animation: false,
            events: {
                redraw: function () {
                    const svgRangesGroup = refreshSVGRangesGroup(this);
                    let additionalIcons: AdditionalToDrawType = {
                        points: [],
                        ranges: [],
                    };
                    additionalIcons = chartRedrawAlerts(this, mainSeriesRef, additionalIcons);

                    additionalIcons.ranges.forEach((range: RangeToDrawType<ChartPoint>) => {
                        drawSVG(appTheme, this, 'range', range, svgRangesGroup);
                    });

                    additionalIcons.points.forEach((point) => {
                        drawSVG(appTheme, this, 'point', point, svgRangesGroup, undefined, true);
                    });

                    adjustCanvas(this, additionalIcons);
                    setMaxChartXValue(this.xAxis[0].getExtremes().dataMax);
                },
            },
        },
        rangeSelector: { enabled: false },
        tooltip: { enabled: false },
        legend: { enabled: false },
        scrollbar: { enabled: false },
        navigator: { enabled: false },
        yAxis: {
            offset: 30,
            labels: {
                formatter: function () {
                    if (!chartData) {
                        return '';
                    }
                    return formatChartValue(this, 'left', chartData, { current: chartData.state });
                },
            },
        },
    };

    const [chartOptions, setChartOptions] = useState<Highcharts.Options>(basicChartOptions);

    useEffect(() => {
        if (!chart) {
            return;
        }
        chart?.xAxis[0].setExtremes(chart?.xAxis[0].getExtremes().dataMin, maxChartXValue);
    }, [chart, maxChartXValue]);

    const mainSeries = useMemo(() => {
        if (!chartData) {
            return;
        }

        const series = processMetricSeries(chartData, { compareLines: [], overlays: [], legend: {} });
        if (options && typeof options.unitToSimulate != 'undefined' && typeof options.intToSimulate != 'undefined') {
            if (series?.length && series[0].data.length > 0) {
                const lastPoint = series[0].data[series[0].data.length - 1];
                const minPoint = subtractRangeFromDate(
                    options.unitToSimulate,
                    options.intToSimulate,
                    new Date(lastPoint.x),
                ).getTime();
                series[0].data = series[0].data.filter((p) => p.x >= minPoint);
            }
        }
        return series;
    }, [chartData]);
    const mainSeriesRef = useRef<Array<ChartSeriesType>>();
    useEffect(() => {
        mainSeriesRef.current = mainSeries;
    }, [mainSeries]);

    useEffect(() => {
        setTimeout(() => {
            if (!chart) {
                return;
            }
            const currentExt = chart.xAxis[0].getExtremes();
            chart.xAxis[0].setExtremes(currentExt.dataMin, currentExt.dataMax);
        }, 100);
    }, [mainSeries]);

    const alertSeries = useMemo(
        () =>
            chart && chartData && simulationId
                ? getAlertSeries(chartData.seriesData, chartData.alertSeriesData, {}, chart, appTheme, undefined, true)
                : [],
        [chartData, chart, chart?.series?.length ?? 0, simulationId],
    );

    useEffect(() => {
        if (!chart) {
            return;
        }
        // Remove ChartRanges lines & Icons
        clearChart(chart);
    }, [chart, alertSeries]);

    useEffect(() => {
        if (!chart || !mainSeries) {
            return;
        }
        let seriesList = mainSeries;
        if (alertSeries?.[0]?.data?.length) {
            seriesList = seriesList.concat(alertSeries);
        }
        //@ts-ignore
        setChartOptions({ ...basicChartOptions, series: seriesList });
    }, [chart, mainSeries, alertSeries]);

    return <Chart afterChartCreated={afterChartCreated} chartOptions={chartOptions} />;
}
